// What? A React component for reviewing document type prompts.
// Why? To allow users to review prompts and structure before generating documents.
// How? The component's logic flow:
// 1. Load: Fetch document type configuration and generate prompts
// 2. Display: Show sections with inputs, source documents, and generated prompts
// 3. View: Access all section prompts in a modal for review and copying

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/joy";
import DescriptionIcon from "@mui/icons-material/Description";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { useFetchDocumentType } from "../../hooks/useDocumentTypes";
import { useTestDocumentType } from "../../hooks/useTestDocumentType";
import TestSection from "../../components/documentType/test/TestSection";
import SectionPromptsModal from "../../components/documentType/test/SectionPromptsModal";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import Page from "../../../../components/Pages/Page";
import PageHeader from "../../../../components/PageHeader";
import Loading from "../../../../components/Loading";
import PageError from "../../../../components/PageError";

const TestDocumentType: React.FC = () => {
  const navigate = useNavigate();
  const { workspace } = useWorkspaceContext();
  const workspace_id = workspace?.id || "";
  const { id } = useParams<{ id: string }>();
  const documentTypeId = id ? parseInt(id, 10) : undefined;

  const {
    documentType,
    loading: loadingDocType,
    error: docTypeError,
  } = useFetchDocumentType(documentTypeId || 0, workspace_id, undefined); // TODO: FIXA HÄR!!
  const {
    testDocumentType,
    loading: testLoading,
    error: testError,
    testResults,
    resetTestResults,
  } = useTestDocumentType(workspace_id);
  const [isPromptsModalOpen, setIsPromptsModalOpen] = useState(false);

  useEffect(() => {
    // Only trigger test if we have the required data and haven't already tested
    if (
      documentTypeId &&
      workspace_id &&
      !testLoading &&
      !testResults &&
      !testError
    ) {
      testDocumentType(documentTypeId).catch((err) => {
        console.error("Test failed:", err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- testDocumentType, testLoading, testResults, testError intentionally excluded to prevent re-triggering
  }, [documentTypeId, workspace_id]);

  // Reset test results when document type changes
  useEffect(() => {
    resetTestResults();
  }, [documentTypeId, resetTestResults]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetTestResults();
    };
  }, [resetTestResults]);

  if (!documentTypeId) {
    return (
      <Page>
        <PageError>Invalid document type ID</PageError>
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Button
            variant="outlined"
            startDecorator={<ArrowBackIcon />}
            onClick={() =>
              navigate(
                `/workspace/${workspace_id}/meridian/admin/document-types`,
              )
            }
          >
            Back to Document Types
          </Button>
        </Box>
      </Page>
    );
  }

  if (!workspace_id) {
    return (
      <Page>
        <PageError>
          Workspace not found. Please ensure you're accessing this page from
          within a workspace.
        </PageError>
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Button
            variant="outlined"
            startDecorator={<ArrowBackIcon />}
            onClick={() =>
              navigate(
                `/workspace/${workspace_id}/meridian/admin/document-types`,
              )
            }
          >
            Back to Document Types
          </Button>
        </Box>
      </Page>
    );
  }

  if (loadingDocType || testLoading) {
    return (
      <Page>
        <PageHeader>
          <Typography level="h1">Review Document Type Prompts</Typography>
        </PageHeader>

        <Typography level="body-sm" sx={{ mb: 2, color: "text.secondary" }}>
          Review the prompts for the document type as they would be used when
          generating documents, or view and copy all section prompts.
        </Typography>

        <Card>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <Loading />
            </Box>
          </CardContent>
        </Card>
      </Page>
    );
  }

  if (testError) {
    return (
      <Page>
        <PageError>{testError}</PageError>
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Button
            variant="outlined"
            startDecorator={<ArrowBackIcon />}
            onClick={() =>
              navigate(
                `/workspace/${workspace_id}/meridian/admin/document-types`,
              )
            }
          >
            Back to Document Types
          </Button>
        </Box>
      </Page>
    );
  }

  // If document type fails to load but test results are available, show test results
  if (docTypeError && !testResults) {
    return (
      <Page>
        <PageHeader>
          <Typography level="h1">Review Document Type Prompts</Typography>
        </PageHeader>

        <Alert color="warning" sx={{ mb: 2 }}>
          Failed to load document type details.
        </Alert>

        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Button
            variant="outlined"
            startDecorator={<ArrowBackIcon />}
            onClick={() =>
              navigate(
                `/workspace/${workspace_id}/meridian/admin/document-types`,
              )
            }
          >
            Back to Document Types
          </Button>
        </Box>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">Review Document Type Prompts</Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flexWrap: "wrap",
            "& > button": { flexShrink: 0 },
          }}
        >
          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<DescriptionIcon />}
            onClick={() => setIsPromptsModalOpen(true)}
          >
            View All Section Prompts
          </Button>
          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<ArrowBackIcon />}
            onClick={() =>
              navigate(
                `/workspace/${workspace_id}/admin/document-types/${documentTypeId}`,
              )
            }
          >
            Back to Edit
          </Button>
        </Stack>
      </PageHeader>

      <Typography level="title-lg" sx={{ mb: 1 }}>
        {documentType?.Name || `Document Type ${documentTypeId}`}
      </Typography>
      <Typography level="body-sm" sx={{ mb: 2, color: "text.secondary" }}>
        Review the prompts for the document type as they would be used when
        generating documents, or view and copy all section prompts.
      </Typography>

      <Card>
        <CardContent>
          {testResults ? (
            testResults.sections && testResults.sections.length > 0 ? (
              testResults.sections.map((section) => (
                <TestSection key={section.sectionId} section={section} />
              ))
            ) : (
              <Box>
                <Typography level="body-sm">
                  No sections found in test results.
                </Typography>
                <Typography
                  level="body-xs"
                  sx={{ mt: 1, fontFamily: "monospace" }}
                >
                  {JSON.stringify(testResults, null, 2)}
                </Typography>
              </Box>
            )
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography
                level="body-lg"
                sx={{ color: "text.secondary", mb: 2 }}
              >
                {testLoading ? "Loading prompts..." : "No prompts available."}
              </Typography>
              {testError && (
                <Alert color="danger" sx={{ mt: 2 }}>
                  {testError}
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <SectionPromptsModal
        isOpen={isPromptsModalOpen}
        onClose={() => setIsPromptsModalOpen(false)}
        documentTypeId={documentTypeId}
        workspace_id={workspace_id}
      />
    </Page>
  );
};

export default TestDocumentType;
